import { supabase } from '@/integrations/supabase/client';

/**
 * Validates the current session and attempts to refresh if needed
 * @returns Promise<boolean> - True if session is valid, false otherwise
 */
export const validateAndRefreshSession = async (): Promise<boolean> => {
  const timestamp = new Date().toISOString();
  
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error(`[${timestamp}] ❌ SESSION_VALIDATION_ERROR:`, sessionError);
      return false;
    }
    
    if (!session) {
      return false;
    }
    
    // Check if session is about to expire (within 1 minute)
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiresAt ? (expiresAt - now) : 0;
    
    // If session is about to expire, refresh it
    if (timeUntilExpiry < 60 && timeUntilExpiry > 0) {
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error(`[${timestamp}] ❌ SESSION_REFRESH_FAILED:`, refreshError);
        return false;
      }
      
      if (refreshedSession) {
        return true;
      }
      
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`[${timestamp}] 💥 SESSION_VALIDATION_EXCEPTION:`, error);
    return false;
  }
};

/**
 * Executes a database query with automatic session validation and retry logic
 * @param queryFn - Function that returns a Supabase query promise
 * @param maxRetries - Maximum number of retry attempts (default: 1)
 * @returns Promise with query result
 */
interface QueryError {
  code?: string;
  message?: string;
}

export const executeWithAuthRetry = async <T>(
  queryFn: () => Promise<{ data: T | null; error: QueryError | null }>,
  maxRetries: number = 1
): Promise<{ data: T | null; error: QueryError | null }> => {
  const timestamp = new Date().toISOString();
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Validate session before query
    if (attempt > 0) {
      const sessionValid = await validateAndRefreshSession();
      if (!sessionValid) {
        break;
      }
    }
    
    const result = await queryFn();
    
    // If successful or non-auth error, return result
    if (!result.error || result.error.code !== 'PGRST301') {
      return result;
    }
    
    // Auth error detected
    if (attempt >= maxRetries) {
      return result;
    }
  }
  
  // This shouldn't be reached, but return error if it does
  return { data: null, error: { message: 'Query failed after retries' } as QueryError };
};